/**
 * L'EPG in forma colonnare.
 *
 * «Non si tiene tutto in memoria come oggetti. Cinquemila oggetti canale con i loro
 * programmi sono più pesanti dei cinque megabyte di partenza. La cache sta su disco; in
 * memoria ci sta una finestra» (07-PRESTAZIONI §3, punto 3).
 *
 * Su dodicimila canali con una dozzina di programmi ciascuno sono centoquarantamila
 * oggetti `Program`, ognuno con quattro stringhe e un'intestazione: qualche decina di
 * megabyte su una macchina che ne ha circa mille in tutto e li condivide con il sistema.
 * Qui gli stessi dati stanno in cinque array tipizzati più due serbatoi di stringhe, e un
 * oggetto `Program` nasce **solo quando qualcosa lo deve disegnare**.
 *
 * Il serbatoio di stringhe è il pezzo che paga di più: i titoli dell'EPG si ripetono —
 * «TG 20», «Replica», il nome della serie su quattro passaggi — e tenerli una volta sola
 * è gratis.
 */

import type { GuideWindow, Program } from './guide.js';

export interface ProgramStoreData {
  /** `channelOffsets[i]` … `channelOffsets[i+1]`: i programmi del canale `i`. */
  readonly channelOffsets: Uint32Array;
  /** Minuti dall'origine. Int32 regge ±4000 anni: non è il limite che ci preoccupa. */
  readonly startMinutes: Int32Array;
  /** Durata in minuti. Uint16 regge 45 giorni. */
  readonly durationMinutes: Uint16Array;
  readonly titleIndex: Uint32Array;
  readonly genreIndex: Uint16Array;
  /**
   * La descrizione, nel suo serbatoio.
   *
   * Ci sta per la stessa ragione dei titoli — si ripete, perché le repliche e le puntate di una
   * serie portano lo stesso testo — e con l'indice a 32 bit perché le descrizioni distinte, a
   * differenza dei generi, possono essere quante sono i programmi.
   *
   * Prima non c'era e `materialize` restituiva la stringa vuota: l'anteprima della guida, che nel
   * prototipo mostra due righe di trama, non aveva **niente** da mostrare. Non era un difetto
   * visibile — era una riga che semplicemente non compariva.
   */
  readonly descriptionIndex: Uint32Array;
  /** Tipologia evento; 0 significa «nessuna». */
  readonly eventKindIndex: Uint8Array;
  readonly titles: ReadonlyArray<string>;
  readonly genres: ReadonlyArray<string>;
  readonly descriptions: ReadonlyArray<string>;
  /** Il primo elemento è la stringa vuota: l'indice 0 vuol dire «nessuna tipologia». */
  readonly eventKinds: ReadonlyArray<string>;
  readonly originMs: number;
  readonly channelIds: ReadonlyArray<string>;
}

export class ProgramStore {
  constructor(private readonly data: ProgramStoreData) {}

  get channelCount(): number {
    return Math.max(0, this.data.channelOffsets.length - 1);
  }

  get programCount(): number {
    return this.data.startMinutes.length;
  }

  programCountOf(channelIndex: number): number {
    const { channelOffsets } = this.data;
    if (channelIndex < 0 || channelIndex + 1 >= channelOffsets.length) return 0;
    return (channelOffsets[channelIndex + 1] as number) - (channelOffsets[channelIndex] as number);
  }

  startMsOf(channelIndex: number, slot: number): number {
    const base = this.data.channelOffsets[channelIndex] as number;
    return this.data.originMs + (this.data.startMinutes[base + slot] as number) * 60_000;
  }

  endMsOf(channelIndex: number, slot: number): number {
    const base = this.data.channelOffsets[channelIndex] as number;
    const index = base + slot;
    return (
      this.data.originMs +
      ((this.data.startMinutes[index] as number) + (this.data.durationMinutes[index] as number)) * 60_000
    );
  }

  /**
   * Materializza un `Program`. È l'unico punto in cui nasce un oggetto, e si chiama solo
   * per quel che si disegna.
   */
  materialize(channelIndex: number, slot: number): Program {
    const { data } = this;
    const base = data.channelOffsets[channelIndex] as number;
    const index = base + slot;
    const startMinutes = data.startMinutes[index] as number;
    const durationMinutes = data.durationMinutes[index] as number;
    const kindIndex = data.eventKindIndex[index] as number;
    return {
      id: `${data.channelIds[channelIndex] ?? channelIndex}-${slot}`,
      channelId: data.channelIds[channelIndex] ?? String(channelIndex),
      title: data.titles[data.titleIndex[index] as number] ?? '',
      genre: data.genres[data.genreIndex[index] as number] ?? '',
      startMs: data.originMs + startMinutes * 60_000,
      endMs: data.originMs + (startMinutes + durationMinutes) * 60_000,
      eventKind: kindIndex === 0 ? null : data.eventKinds[kindIndex] ?? null,
      description: data.descriptions[data.descriptionIndex[index] as number] ?? '',
    };
  }

  /**
   * L'intervallo di slot che tocca la finestra, `[from, to)`.
   *
   * I programmi di un canale sono ordinati e contigui, quindi si trova per bisezione
   * invece che scorrendoli: su una riga da cinquanta programmi la differenza non si vede,
   * ma questa chiamata si fa per ogni riga visibile a ogni scorrimento.
   */
  slotsInWindow(channelIndex: number, span: GuideWindow): readonly [number, number] {
    const count = this.programCountOf(channelIndex);
    if (count === 0) return [0, 0];

    const startMinuteOf = (slot: number): number => {
      const base = this.data.channelOffsets[channelIndex] as number;
      return this.data.startMinutes[base + slot] as number;
    };
    const endMinuteOf = (slot: number): number => {
      const base = this.data.channelOffsets[channelIndex] as number;
      const index = base + slot;
      return (this.data.startMinutes[index] as number) + (this.data.durationMinutes[index] as number);
    };

    const windowStartMinute = (span.startMs - this.data.originMs) / 60_000;
    const windowEndMinute = (span.endMs - this.data.originMs) / 60_000;

    // Primo slot che finisce dopo l'inizio della finestra.
    let low = 0;
    let high = count;
    while (low < high) {
      const mid = (low + high) >> 1;
      if (endMinuteOf(mid) <= windowStartMinute) low = mid + 1;
      else high = mid;
    }
    const from = low;

    // Primo slot che comincia dopo la fine della finestra.
    low = from;
    high = count;
    while (low < high) {
      const mid = (low + high) >> 1;
      if (startMinuteOf(mid) < windowEndMinute) low = mid + 1;
      else high = mid;
    }
    return [from, low];
  }

  /** Lo slot in onda a un certo istante, o `-1`. */
  slotAt(channelIndex: number, atMs: number): number {
    const [from, to] = this.slotsInWindow(channelIndex, { startMs: atMs, endMs: atMs + 1 });
    return to > from ? from : -1;
  }

  /** Gli identificativi dei canali, nell'ordine in cui questo magazzino li indicizza. */
  get channelIds(): ReadonlyArray<string> {
    return this.data.channelIds;
  }

  /**
   * Un magazzino ristretto ad alcuni canali, nell'ordine dato.
   *
   * ## Perché serve, e perché non basta filtrare l'elenco dei canali
   *
   * La guida filtrata per categoria mostra un sottoinsieme, e il cursore indicizza **quel**
   * sottoinsieme. Il magazzino però è indicizzato sull'elenco intero: passare al disegno un elenco
   * corto e un magazzino lungo significa leggere i programmi del canale sbagliato — e il difetto non
   * si presenta mai con «Tutti» attivo, cioè non si presenta in sviluppo. Compare quando un cliente
   * sceglie «Sport» e vede accanto a Sky Sport la programmazione di Rai 1.
   *
   * ## Perché si copia invece di tenere una mappa di indici
   *
   * Una mappa sarebbe più economica e va sbagliata in un punto solo per rompere tutto: ogni metodo
   * dovrebbe ricordarsi di tradurre, e quello che se ne dimentica sbaglia **in silenzio**. Qui il
   * sottoinsieme è un magazzino come gli altri, quindi nessun chiamante deve sapere niente.
   *
   * Il costo si paga a ogni cambio di categoria, che è un gesto deliberato — non a ogni freccia. E
   * `select` dell'insieme intero restituisce `this`: il caso più comune non copia nulla.
   */
  select(channelIndices: ReadonlyArray<number>): ProgramStore {
    if (channelIndices.length === this.channelCount && channelIndices.every((at, i) => at === i)) {
      return this;
    }

    const { data } = this;
    const offsets: number[] = [0];
    const startMinutes: number[] = [];
    const durationMinutes: number[] = [];
    const titleIndex: number[] = [];
    const genreIndex: number[] = [];
    const descriptionIndex: number[] = [];
    const eventKindIndex: number[] = [];
    const channelIds: string[] = [];

    for (const channelIndex of channelIndices) {
      channelIds.push(data.channelIds[channelIndex] ?? String(channelIndex));
      const base = data.channelOffsets[channelIndex] as number;
      const count = this.programCountOf(channelIndex);
      for (let slot = 0; slot < count; slot += 1) {
        const at = base + slot;
        startMinutes.push(data.startMinutes[at] as number);
        durationMinutes.push(data.durationMinutes[at] as number);
        titleIndex.push(data.titleIndex[at] as number);
        genreIndex.push(data.genreIndex[at] as number);
        descriptionIndex.push(data.descriptionIndex[at] as number);
        eventKindIndex.push(data.eventKindIndex[at] as number);
      }
      offsets.push(startMinutes.length);
    }

    return new ProgramStore({
      channelOffsets: Uint32Array.from(offsets),
      startMinutes: Int32Array.from(startMinutes),
      durationMinutes: Uint16Array.from(durationMinutes),
      titleIndex: Uint32Array.from(titleIndex),
      genreIndex: Uint16Array.from(genreIndex),
      descriptionIndex: Uint32Array.from(descriptionIndex),
      eventKindIndex: Uint8Array.from(eventKindIndex),
      // I serbatoi si **condividono**: sono immutabili, e ricopiarli vanificherebbe il risparmio per
      // cui esistono. Gli indici continuano a puntare alle stesse stringhe.
      titles: data.titles,
      genres: data.genres,
      descriptions: data.descriptions,
      eventKinds: data.eventKinds,
      originMs: data.originMs,
      channelIds,
    });
  }
}

/** Costruttore d'appoggio: raccoglie i programmi e li impacchetta nella forma colonnare. */
export class ProgramStoreBuilder {
  private readonly offsets: number[] = [0];
  private readonly startMinutes: number[] = [];
  private readonly durationMinutes: number[] = [];
  private readonly titleIdx: number[] = [];
  private readonly genreIdx: number[] = [];
  private readonly descriptionIdx: number[] = [];
  private readonly kindIdx: number[] = [];
  private readonly titles: string[] = [];
  private readonly genres: string[] = [];
  private readonly descriptions: string[] = [];
  private readonly eventKinds: string[] = [''];
  private readonly titlePool = new Map<string, number>();
  private readonly genrePool = new Map<string, number>();
  private readonly descriptionPool = new Map<string, number>();
  private readonly kindPool = new Map<string, number>([['', 0]]);
  private readonly channelIds: string[] = [];

  constructor(private readonly originMs: number) {}

  private intern(value: string, pool: Map<string, number>, list: string[]): number {
    const existing = pool.get(value);
    if (existing !== undefined) return existing;
    const index = list.length;
    list.push(value);
    pool.set(value, index);
    return index;
  }

  /**
   * Aggiunge i programmi di un canale, **ordinandoli e togliendo i doppioni**.
   *
   * ## Da requisito a garanzia
   *
   * Prima qui c'era scritto «i programmi vanno passati ordinati per inizio: la ricerca per bisezione
   * lo dà per scontato, e un elenco disordinato darebbe risultati sbagliati in silenzio». Era vero, e
   * **nessuno lo soddisfaceva**: l'adattatore restituisce l'ordine in cui l'API risponde, la cache lo
   * archivia così, e chi costruisce il magazzino lo passava intatto.
   *
   * Il risultato è il difetto che si è visto sullo schermo: la fascia del lettore scriveva «Poi:» con
   * il titolo del programma **in corso**, perché la bisezione su un elenco disordinato risponde con
   * la posizione sbagliata senza protestare.
   *
   * Un presupposto che nessuno controlla non è un presupposto, è una speranza. Qui si **garantisce**,
   * e costa un ordinamento su una dozzina di elementi per canale, fatto quando arrivano dati nuovi —
   * cioè di rado.
   *
   * ## E i doppioni
   *
   * Due voci con lo stesso inizio sono lo stesso programma detto due volte: capita quando due
   * finestre si sovrappongono o quando la guida del fornitore ripete una riga. Lasciarli passa
   * inosservato ovunque tranne che nel «Poi», dove il programma successivo diventa quello attuale.
   */
  addChannel(
    channelId: string,
    programs: ReadonlyArray<{
      startMs: number;
      endMs: number;
      title: string;
      genre: string;
      eventKind: string | null;
      /** Facoltativa: un EPG che non la porta dà la stringa vuota, non una riga inventata. */
      description?: string;
    }>,
  ): void {
    this.channelIds.push(channelId);
    /*
      Si ordina e si deduplica **qui**, una volta, invece di chiederlo a ogni chiamante.

      La copia è voluta: l'elenco che arriva è di chi l'ha passato, e ordinarlo sul posto cambierebbe
      un dato altrui — che per una cache archiviata significa riscriverla a sorpresa.
    */
    const ordered = [...programs].sort((a, b) => a.startMs - b.startMs);
    let previousStartMs: number | null = null;
    for (const program of ordered) {
      if (previousStartMs !== null && program.startMs === previousStartMs) continue;
      previousStartMs = program.startMs;
      const startMinute = Math.round((program.startMs - this.originMs) / 60_000);
      const duration = Math.round((program.endMs - program.startMs) / 60_000);
      if (duration <= 0 || duration > 65_535) {
        throw new Error(`ProgramStoreBuilder: durata fuori scala per ${channelId} (${duration} min)`);
      }
      this.startMinutes.push(startMinute);
      this.durationMinutes.push(duration);
      this.titleIdx.push(this.intern(program.title, this.titlePool, this.titles));
      this.genreIdx.push(this.intern(program.genre, this.genrePool, this.genres));
      this.kindIdx.push(this.intern(program.eventKind ?? '', this.kindPool, this.eventKinds));
      this.descriptionIdx.push(
        this.intern(program.description ?? '', this.descriptionPool, this.descriptions),
      );
    }
    this.offsets.push(this.startMinutes.length);
  }

  build(): ProgramStore {
    if (this.genres.length > 65_535) throw new Error('ProgramStoreBuilder: troppi generi');
    if (this.eventKinds.length > 255) throw new Error('ProgramStoreBuilder: troppe tipologie di evento');
    return new ProgramStore({
      channelOffsets: Uint32Array.from(this.offsets),
      startMinutes: Int32Array.from(this.startMinutes),
      durationMinutes: Uint16Array.from(this.durationMinutes),
      titleIndex: Uint32Array.from(this.titleIdx),
      genreIndex: Uint16Array.from(this.genreIdx),
      descriptionIndex: Uint32Array.from(this.descriptionIdx),
      eventKindIndex: Uint8Array.from(this.kindIdx),
      titles: this.titles,
      genres: this.genres,
      descriptions: this.descriptions,
      eventKinds: this.eventKinds,
      originMs: this.originMs,
      channelIds: this.channelIds,
    });
  }
}
